import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowRight, Lightbulb, Wallet, Clock, TrendingUp, Shield,
  CheckCircle2, ArrowLeftRight
} from 'lucide-react';
import { inventorsPageContent } from '../data/mock';

const iconMap = {
  Wallet, Clock, TrendingUp, Shield
};

const InventorsPage = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white pt-16 pb-24">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#1e3a5f]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-20 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Lightbulb className="w-4 h-4" />
                <span>For Independent Inventors</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-[#0f1f2e] leading-tight mb-6">
                {inventorsPageContent.hero.headline}
              </h1>
              
              <p className="text-lg text-slate-600 leading-relaxed mb-10">
                {inventorsPageContent.hero.subheadline}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/request-access">
                  <Button size="lg" className="bg-[#1e3a5f] hover:bg-[#2a4a73] text-white px-8 py-6 text-base shadow-xl shadow-[#1e3a5f]/20 transition-all duration-300 hover:-translate-y-0.5">
                    Request Evaluation
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/how-it-works">
                  <Button size="lg" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-6 text-base">
                    See How It Works
                  </Button>
                </Link>
              </div>
            </div>

            {/* Visual */}
            <div className="hidden lg:block">
              <Card className="bg-white border-slate-200 shadow-2xl">
                <CardContent className="p-8">
                  <h3 className="font-semibold text-[#0f1f2e] mb-6 text-center">The Inventor's Challenge</h3>
                  
                  <div className="bg-slate-50 rounded-xl p-6 mb-6 border border-slate-200">
                    <div className="text-center mb-3">
                      <div className="text-lg font-medium text-slate-700">Traditional Path</div>
                    </div>
                    <div className="text-sm text-slate-600 text-center">
                      Large upfront costs, personal financial exposure, and pressure to monetize before value is clear.
                    </div>
                  </div>

                  <div className="flex items-center justify-center space-x-4 my-6">
                    <div className="flex-1 h-0.5 bg-slate-200" />
                    <ArrowLeftRight className="w-6 h-6 text-emerald-500" />
                    <div className="flex-1 h-0.5 bg-slate-200" />
                  </div>

                  <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
                    <div className="text-center mb-3">
                      <div className="text-lg font-medium text-emerald-800">IpFunders Model</div>
                    </div>
                    <div className="text-sm text-emerald-700 text-center">
                      Upfront cost shifts to us. Personal exposure stays limited. Commitment grows only as the IP matures.
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f1f2e] mb-4">
              What IpFunders Offers Inventors
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Professional IP development without personal financial exposure
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {inventorsPageContent.features.map((feature, idx) => {
              const Icon = iconMap[feature.icon];
              return (
                <Card key={idx} className="bg-white border-slate-200 hover:border-[#1e3a5f]/30 hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-[#1e3a5f]/10 rounded-xl flex items-center justify-center mb-5">
                      <Icon className="w-6 h-6 text-[#1e3a5f]" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#0f1f2e] mb-2">{feature.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works for Inventors */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0f1f2e] mb-4">
              How It Works for You
            </h2>
          </div>

          <Card className="bg-white border-slate-200 shadow-lg">
            <CardContent className="p-8">
              <div className="space-y-6">
                {[
                  { step: 1, title: 'Submit Your Concept', description: 'Share a high-level overview. We assess technical merit and strategic potential.' },
                  { step: 2, title: 'Establish Confidentiality', description: 'If promising, we sign a mutual NDA and conduct deeper evaluation.' },
                  { step: 3, title: 'Funding Decision', description: 'We decide whether to fund protection and development of your IP.' },
                  { step: 4, title: 'We Handle Prosecution', description: 'We manage the patent process, shifting upfront cost and uncertainty to us.' },
                  { step: 5, title: 'Investment Aligns With Progress', description: 'Commitment grows as the IP matures. You retain control throughout.' }
                ].map((item) => (
                  <div key={item.step} className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-[#1e3a5f] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">{item.step}</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#0f1f2e]">{item.title}</h4>
                      <p className="text-slate-500 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0f1f2e]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Have an Idea Worth Protecting?
          </h2>
          <p className="text-lg text-slate-300 mb-10">
            Submit your concept for evaluation. We'll assess fit and explain how the model works.
          </p>
          <Link to="/request-access">
            <Button size="lg" className="bg-white text-[#0f1f2e] hover:bg-slate-100 px-10 py-6 text-base font-semibold shadow-xl transition-all duration-300 hover:-translate-y-0.5">
              Request Evaluation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default InventorsPage;
